// ============================================================================
// Lead Router — routing config (FastAPI + MongoDB backend, nativo Aesthetic OS)
// ----------------------------------------------------------------------------
// SICUREZZA / ARCHITETTURA:
// - Il frontend NON chiama mai Meta API o WhatsApp direttamente.
// - Nessuna sessione/numero WhatsApp viene salvato nel browser.
// - Account routings + campaign overrides + pending mappings sono backend-backed
//   (idratati una volta, mutazioni ottimistiche).
// - I template WhatsApp Welcome e i relativi log sono serviti dal backend
//   (/api/whatsapp/*) tramite adminApi — nessun localStorage, invio disattivato.
//
// Flusso lead nativo Aesthetic OS/MongoDB (POST /api/ingest/leads):
//   nuovo lead -> resolve_route (override campagna -> routing account -> pending)
//   -> lead salvato e visibile nel Lead Manager. Nessuna dipendenza esterna.
// ============================================================================

import { CLIENTS, clientById } from "@/data/mockData";
import { BUSINESS_MANAGERS, bmOfAccount } from "@/data/businessManagers";
import { api } from "@/lib/api";

// Business Manager ID numerici (mock, stile Meta).
const BM_META_ID = {
  bm_principale: "1029384756",
  bm_secondario: "5647382910",
};

export const bmMetaId = (bmId) => BM_META_ID[bmId] || "—";
export const bmById = (bmId) => BUSINESS_MANAGERS.find((b) => b.id === bmId);
export const bmIdOfClient = (client) => bmOfAccount[client.adAccountId];

// Righe di routing derivate dai clienti (1 account pubblicitario per cliente).
export const ROUTING_ROWS = CLIENTS.map((c) => {
  const bmId = bmOfAccount[c.adAccountId];
  const bm = BUSINESS_MANAGERS.find((b) => b.id === bmId);
  return {
    client_id: c.id,
    client_name: c.name,
    business_manager_internal_id: bmId,
    business_manager_id: bmMetaId(bmId),
    business_manager_name: bm?.name || "—",
    ad_account_id: c.adAccountId,
    ad_account_name: c.name,
  };
});

export const CAMPAIGN_STATUS = {
  active: { tone: "ok", label: "Attiva" },
  paused: { tone: "neutral", label: "In pausa" },
};

// ===========================================================================
// LEAD ROUTER — real backend cache (Phase 2 · Step 3)
// Account routings + campaign overrides + pending mappings are hydrated once
// from the backend at auth bootstrap. Sync getters read the cache so the
// existing UI keeps working; mutations are optimistic + persisted in background.
// ===========================================================================
let _routings = [];
let _overrides = [];
let _pending = [];
// Catalogo Meta REALE (BM/account) sincronizzato dal backend (meta_assets). Nessun mock operativo.
let _catalog = { business_managers: [] };

export const hydrateMetaCatalog = async () => {
  try { const { data } = await api.get("/routing/asset-catalog"); _catalog = data && Array.isArray(data.business_managers) ? data : { business_managers: [] }; }
  catch { _catalog = { business_managers: [] }; }
  lrNotify();
  return _catalog;
};
export const getCatalogBMs = () => _catalog.business_managers || [];
export const getCatalogAccounts = (bmId) => (getCatalogBMs().find((b) => b.id === bmId)?.accounts) || [];
export const fetchAssetCampaigns = (adAccountId) =>
  api.get("/routing/asset-campaigns", { params: adAccountId ? { ad_account_id: adAccountId } : {} }).then((r) => r.data).catch(() => []);
let _lrHydrated = false;
const _lrSubs = new Set();
const lrNotify = () => _lrSubs.forEach((fn) => { try { fn(); } catch { /* noop */ } });

export const subscribeLeadRouter = (fn) => { _lrSubs.add(fn); return () => _lrSubs.delete(fn); };
export const isLeadRouterReady = () => _lrHydrated;

export async function hydrateLeadRouter(perms = []) {
  const canManage = perms.includes("manage_lead_router");
  try {
    const [ar, ov, pm] = await Promise.all([
      api.get("/routing/account-routings").then((r) => r.data).catch(() => []),
      api.get("/routing/campaign-overrides").then((r) => r.data).catch(() => []),
      canManage ? api.get("/routing/pending-mappings").then((r) => r.data).catch(() => []) : Promise.resolve([]),
    ]);
    _routings = Array.isArray(ar) ? ar : [];
    _overrides = Array.isArray(ov) ? ov : [];
    _pending = Array.isArray(pm) ? pm : [];
    hydrateMetaCatalog();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("hydrateLeadRouter failed", e?.response?.status);
  } finally {
    _lrHydrated = true;
    lrNotify();
  }
}

export const resetLeadRouter = () => { _routings = []; _overrides = []; _pending = []; _catalog = { business_managers: [] }; _lrHydrated = false; };
const lrResync = () => { hydrateLeadRouter(); };

// ---- ingestion sandbox (internal/testing) ----
export const ingestTestLead = async (payload) => {
  const { data } = await api.post("/ingest/leads", payload);
  // refresh caches so pending tab / lead manager reflect the new lead
  hydrateLeadRouter();
  try { const { hydrateLeads } = await import("@/lib/leadsData"); hydrateLeads(); } catch { /* noop */ }
  return data;
};

// ---- pending mappings ----
export const getPendingMappings = () => _pending.filter((p) => p.status === "pending");

export const resolvePendingMapping = (pendingId, payload) => {
  _pending = _pending.map((p) => (p.id === pendingId ? { ...p, status: "resolved" } : p));
  lrNotify();
  return api.post(`/routing/pending-mappings/${pendingId}/resolve`, payload)
    .then((r) => { lrResync(); return { ok: true, data: r.data }; })
    .catch((e) => { lrResync(); return { ok: false, error: e?.response?.data?.detail }; });
};

// ---------------------------------------------------------------------------
// CAMPAIGN OVERRIDES (lead_campaign_assignments) — backend-backed.
// ---------------------------------------------------------------------------
export const getAssignments = () => _overrides;

export const assignmentsForClient = (clientId) =>
  _overrides.filter((a) => a.client_id === clientId && a.is_active);

export const assignmentCount = (clientId) => assignmentsForClient(clientId).length;

// Una campagna può essere attiva su un solo cliente alla volta.
export const campaignActiveAssignment = (campaignId) =>
  _overrides.find((a) => a.campaign_id === campaignId && a.is_active) || null;

export const addAssignment = ({ clientId, campaign, adAccountId, bmName, adAccountName, author }) => {
  const existing = campaignActiveAssignment(campaign.id);
  if (existing) return { ok: false, conflictClient: existing.client_name };
  const client = clientById(clientId);
  const tmpId = `lca_tmp_${Date.now()}`;
  _overrides = [..._overrides, {
    id: tmpId, client_id: clientId, client_name: client?.name || "—",
    business_manager_id: "—", business_manager_name: bmName || "—",
    ad_account_id: adAccountId, ad_account_name: adAccountName || adAccountId,
    campaign_id: campaign.id, campaign_name: campaign.name, campaign_status: campaign.status,
    is_active: true, status: "active", created_by: author || "Operatore",
    created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
  }];
  lrNotify();
  api.post("/routing/campaign-overrides", {
    client_id: clientId, client_name: client?.name, ad_account_id: adAccountId,
    campaign_id: campaign.id, campaign_name: campaign.name, campaign_status: campaign.status,
  }).then(({ data }) => {
    _overrides = _overrides.map((a) => (a.id === tmpId ? { ...a, ...data } : a));
    lrNotify();
  }).catch(lrResync);
  return { ok: true };
};

export const removeAssignment = (id) => {
  _overrides = _overrides.filter((a) => a.id !== id);
  lrNotify();
  api.delete(`/routing/campaign-overrides/${id}`).catch(lrResync);
};

// ---------------------------------------------------------------------------
// WHATSAPP WELCOME — vedi adminApi.wa* (/api/whatsapp/*). Nessun localStorage,
// nessuna connessione/numero, invio disattivato lato backend (send_enabled=false).
// Variabili disponibili nei template.
// ---------------------------------------------------------------------------
export const WELCOME_VARIABLES = ["{{first_name}}", "{{full_name}}", "{{campaign_name}}", "{{client_name}}"];

// ---------------------------------------------------------------------------
// ACCOUNT ROUTING (client_account_routings) — backend-backed.
// La visibilità UI resta gestita da VisibilityContext; qui manteniamo is_visible.
// ---------------------------------------------------------------------------
export const getAccountRoutings = () => _routings;

export const accountRoutingForClient = (clientId) =>
  _routings.find((r) => r.client_id === clientId && r.is_active) || null;

export const accountActiveAssignment = (adAccountId) =>
  _routings.find((r) => r.ad_account_id === adAccountId && r.is_active) || null;

export const upsertAccountRouting = ({ id, clientId, clientName, adAccountId, bmId, bmName, adAccountName, author }) => {
  const conflict = _routings.find(
    (r) => r.ad_account_id === adAccountId && r.is_active && r.client_id !== clientId
  );
  if (conflict) return { ok: false, conflictClient: conflict.client_name };

  const rid = id || `car_${clientId}_${Date.now()}`;
  const record = {
    id: rid, client_id: clientId, client_name: clientName,
    business_manager_internal_id: bmId, business_manager_id: "—", business_manager_name: bmName || "—",
    ad_account_id: adAccountId, ad_account_name: adAccountName || adAccountId,
    is_visible: true, is_active: true, status: "active",
    created_by: author || "Operatore", created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
  };
  const idx = _routings.findIndex((r) => r.id === rid);
  if (idx === -1) _routings = [..._routings, record];
  else _routings = _routings.map((r) => (r.id === rid ? { ...r, ...record } : r));
  lrNotify();
  api.post("/routing/account-routings", {
    id: rid, client_id: clientId, client_name: clientName,
    business_manager_id: bmId, ad_account_id: adAccountId, ad_account_name: adAccountName || adAccountId,
  }).then(({ data }) => {
    _routings = _routings.map((r) => (r.id === rid ? { ...r, ...data } : r));
    lrNotify();
  }).catch(lrResync);
  return { ok: true };
};

export const removeAccountRouting = (id) => {
  _routings = _routings.filter((r) => r.id !== id);
  lrNotify();
  api.delete(`/routing/account-routings/${id}`).catch(lrResync);
};

// Campagne associate a un cliente: account principale (routing) + extra campaigns.
export const clientCampaignOptions = (clientId) => {
  const out = [];
  const seen = new Set();
  const push = (cid, cname) => {
    if (cid && !seen.has(cid)) { seen.add(cid); out.push({ campaign_id: cid, campaign_name: cname }); }
  };
  const routing = accountRoutingForClient(clientId);
  if (routing) {
    const acctClient = CLIENTS.find((c) => c.adAccountId === routing.ad_account_id);
    acctClient?.meta.campaigns.forEach((cp) => push(cp.id, cp.name));
  }
  assignmentsForClient(clientId).forEach((a) => push(a.campaign_id, a.campaign_name));
  return out;
};

// --- Account pubblicitari CONDIVISI: mapping canonico campagna -> cliente ---

export const getSharedAccounts = () => api.get("/routing/shared-accounts").then((r) => r.data);
export const getAccountCampaigns = (adAccountId) =>
  api.get(`/routing/account-campaigns?ad_account_id=${encodeURIComponent(adAccountId)}`).then((r) => r.data);
export const assignCampaignToClient = (payload) =>
  api.post("/routing/campaign-overrides", payload).then((r) => r.data);
export const syncCampaignsFromMeta = (month) =>
  api.post("/routing/sync-campaigns", month ? { month } : {}).then((r) => r.data);
export const getCampaignDuplicatesDryrun = () =>
  api.get("/routing/campaign-duplicates-dryrun").then((r) => r.data);
